import globalAxios from "axios";
import { getReviewQaHttp, isMixedReviewEnabled } from "./revisionV2Api";
import {
  findLegacyCatalogName,
  normalizeLegacyCatalogId,
} from "./revisionRapidaSaludJornadaContext";

const axios = Object.fromEntries(["get", "patch", "post"].map(method => [method, (...args) =>
  (isMixedReviewEnabled() ? getReviewQaHttp() : globalAxios)[method](...args)]));

export const REVISION_RAPIDA_SALUD_API = "/revision-rapida/salud";

const unwrapData = (response) => response?.data?.data ?? response?.data;

const convocatoriaPath = (convocatoriaId) =>
  `${REVISION_RAPIDA_SALUD_API}/convocatorias/${encodeURIComponent(String(convocatoriaId))}`;

const RECORDS_QUERY_KEYS = Object.freeze([
  "page",
  "pageSize",
  "state",
  "source",
  "search",
  "sort",
  "order",
]);

const compactRecordsParams = (params = {}) =>
  RECORDS_QUERY_KEYS.reduce((result, key) => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== "") result[key] = value;
    return result;
  }, {});

export const getRevisionRapidaSaludSummary = async (convocatoriaId) =>
  unwrapData(await axios.get(`${convocatoriaPath(convocatoriaId)}/resumen`));

export const getRevisionRapidaSaludMeta = async (convocatoriaId) =>
  unwrapData(await axios.get(`${convocatoriaPath(convocatoriaId)}/meta`));

export const updateRevisionRapidaSaludMeta = async (convocatoriaId, payload) =>
  unwrapData(await axios.patch(`${convocatoriaPath(convocatoriaId)}/meta`, payload));

export const getRevisionRapidaSaludMaterials = async (convocatoriaId) =>
  unwrapData(await axios.get(`${convocatoriaPath(convocatoriaId)}/materiales`));

export const getRevisionRapidaSaludRecords = async (convocatoriaId, params = {}) => {
  const response = await axios.get(`${convocatoriaPath(convocatoriaId)}/registros`, {
    params: compactRecordsParams(params),
  });
  const payload = response?.data || {};

  return {
    data: Array.isArray(payload.data) ? payload.data : [],
    pagination: payload.pagination || {
      page: 1,
      pageSize: 25,
      total: 0,
      totalPages: 0,
    },
  };
};

const recordPath = (recordId) =>
  `${REVISION_RAPIDA_SALUD_API}/registros/${encodeURIComponent(String(recordId))}`;

export const getRevisionRapidaSaludRecordDetail = async (recordId) =>
  unwrapData(await axios.get(recordPath(recordId)));

export const getRevisionConvocatoriaByProject = async (
  convocatoriaId,
  projectId,
  { signal } = {}
) => {
  const normalizedProjectId = normalizeLegacyCatalogId(projectId);
  const normalizedConvocatoriaId = normalizeLegacyCatalogId(convocatoriaId);
  if (!normalizedProjectId || !normalizedConvocatoriaId) return null;

  const response = await axios.get("/convocatoriaproyectos/", {
    params: { id: 2, idProyecto: normalizedProjectId },
    signal,
  });
  const rows = response?.data?.data ?? response?.data;
  if (!Array.isArray(rows)) return null;
  return rows.find(
    (item) => normalizeLegacyCatalogId(item?.id) === normalizedConvocatoriaId
  ) || null;
};

export const getRevisionJornadaCatalogNames = async (context = {}, { signal } = {}) => {
  const estadoId = normalizeLegacyCatalogId(context.estadoConvo);
  const municipioId = normalizeLegacyCatalogId(context.municipio);
  const parroquiaId = normalizeLegacyCatalogId(context.parroquia);
  const requests = [
    estadoId ? axios.get("/getEstados/", { signal }) : Promise.resolve(null),
    estadoId
      ? axios.get("/getMunicipios/", { params: { id: estadoId }, signal })
      : Promise.resolve(null),
    municipioId
      ? axios.get("/getParroquias/", { params: { id: municipioId }, signal })
      : Promise.resolve(null),
  ];
  const [estadosResult, municipiosResult, parroquiasResult] = await Promise.allSettled(requests);
  const rowsFrom = (result) => {
    if (result.status !== "fulfilled") return [];
    const payload = result.value?.data?.data ?? result.value?.data;
    return Array.isArray(payload) ? payload : [];
  };

  return {
    estadoNombre: findLegacyCatalogName(rowsFrom(estadosResult), estadoId, "idestado"),
    municipioNombre: findLegacyCatalogName(rowsFrom(municipiosResult), municipioId, "idmunicipios"),
    parroquiaNombre: findLegacyCatalogName(rowsFrom(parroquiasResult), parroquiaId, "idparroquias"),
  };
};

export const updateRevisionRapidaSaludRecord = async (recordId, payload) =>
  unwrapData(await axios.patch(recordPath(recordId), payload));

export const createRevisionRapidaSaludMaterial = async (convocatoriaId, payload) =>
  unwrapData(await axios.post(`${convocatoriaPath(convocatoriaId)}/materiales`, payload));

export const updateRevisionRapidaSaludMaterial = async (
  convocatoriaId,
  materialId,
  payload
) =>
  unwrapData(
    await axios.patch(
      `${convocatoriaPath(convocatoriaId)}/materiales/${encodeURIComponent(String(materialId))}`,
      payload
    )
  );
